@if (isLoading) {
<div class="overlay">
  <div class="spinner"></div>
  <p class="text-load">&nbsp;&nbsp; Loading...</p>
</div>
}

<!-- ─── Breadcrumb Bar ─── -->
<div class="breadcrumb-bar" [class.blur]="isLoading">
  <div class="breadcrumb">
    <a class="breadcrumb-link" [routerLink]="['/review-management']">Review Management</a>
    <span class="breadcrumb-sep" aria-hidden="true"></span>
    <span class="breadcrumb-current">{{ summary.reviewId }}</span>
  </div>
</div>

<main class="main-content">

  @if (successToastMessage) {
<div class="draft-success-toast">
  <span class="toast-check" aria-hidden="true">&#10003;</span>
  <div class="toast-text">
    <div class="toast-title">Success</div>
    <div class="toast-body">{{ successToastMessage }}</div>
  </div>
  <span class="toast-close" (click)="closeSuccessToast()" aria-hidden="true">&times;</span>
</div>
}

  <!-- ─── Review Process Summary Card ─── -->
  <div class="process-card">
    <div class="process-card-header">
      <h2 class="process-card-title">Review Process Summary</h2>
      <button class="btn-outline-danger" [disabled]="isCancelled || isViewMode || (!isAdminRole && !isAssignedToReview)" (click)="openCancelReviewModal()">Cancel Review</button>
    </div>

    <div class="process-card-divider"></div>

    <!-- Dark summary panel — columns 1 and 3 share most rows; only column 2 and
         one conditional row in column 1 differ between standard and Go-Live. -->
    <div class="summary-panel">
      <!-- Column 1: identity fields -->
      <div class="summary-col">
        <div class="summary-row"><span class="summary-label">Review ID</span><span
            class="summary-value summary-value--strong">{{ summary.reviewId }}</span></div>
        <div class="summary-row"><span class="summary-label">Review Name</span><span class="summary-value">{{
            summary.reviewName }}</span></div>
        @if (isGoLive) {
        <div class="summary-row"><span class="summary-label">Review Subtype</span><span class="summary-value">{{
            summary.typeOfReview === 'pre-post' ? 'Pre & Post Go-Live' :
            summary.typeOfReview === 'post-only' ? 'Post Go-Live' :
            summary.typeOfReview }}</span></div>
        }
        <div class="summary-row"><span class="summary-label">Client Name</span><span class="summary-value">{{
            summary.clientName }}</span></div>
        <div class="summary-row"><span class="summary-label">Market</span><span class="summary-value">{{ summary.market
            }}</span></div>
        <div class="summary-row"><span class="summary-label">Market Unit</span><span class="summary-value">{{
            summary.marketUnit }}</span></div>
      </div>

      <!-- Column 2: location / date fields — differ by review type -->
      <div class="summary-col">
        <div class="summary-row" [class.summary-row--wide]="isGoLive"><span class="summary-label">Location to be Reviewed</span><span
            class="summary-value">{{ summary.locationToBeReviewed }}</span></div>
        <div class="summary-row" [class.summary-row--wide]="isGoLive"><span class="summary-label">Country</span><span class="summary-value">{{
            summary.country }}</span></div>
        @if (isGoLive) {
        @if (summary.typeOfReview !== 'post-only') {
        <div class="summary-row summary-row--wide"><span class="summary-label">Pre Go-Live Fieldwork Start Date</span><span
            class="summary-value">{{ summary.preGoLiveFWStartDate | date:'dd-MMM-yyyy' }}</span></div>
        <div class="summary-row summary-row--wide"><span class="summary-label">Pre Go-Live Fieldwork End Date</span><span
            class="summary-value">{{ summary.preGoLiveFWEndDate | date:'dd-MMM-yyyy' }}</span></div>
        }
        <div class="summary-row summary-row--wide"><span class="summary-label">Post Go-Live Fieldwork Start Date</span><span
            class="summary-value">{{ summary.postGoLiveFWStartDate | date:'dd-MMM-yyyy' }}</span></div>
        <div class="summary-row summary-row--wide"><span class="summary-label">Post Go-Live Fieldwork End Date</span><span
            class="summary-value">{{ summary.postGoLiveFWEndDate | date:'dd-MMM-yyyy' }}</span></div>
        } @else {
        <div class="summary-row"><span class="summary-label">Proposed Month</span><span class="summary-value">{{
            summary.proposedMonth }}</span></div>
        <div class="summary-row"><span class="summary-label">Plan Fieldwork Start Date</span><span
            class="summary-value">{{ summary.planFieldworkStartDate | date:'dd-MMM-yyyy' }}</span></div>
        <div class="summary-row"><span class="summary-label">Plan Fieldwork End Date</span><span
            class="summary-value">{{ summary.planFieldworkEndDate | date:'dd-MMM-yyyy' }}</span></div>
        }
      </div>

      <!-- Column 3: R&C team + status — identical for both review types -->
      <div class="summary-col">
        <div class="summary-row"><span class="summary-label">R&amp;C Reviewer</span><span class="summary-value">{{
            summary.rcReviewer }}</span></div>
        <div class="summary-row"><span class="summary-label">R&amp;C Supervisor</span><span class="summary-value">{{
            summary.rcSupervisor }}</span></div>
        <div class="summary-row"><span class="summary-label">R&amp;C Manager</span><span class="summary-value">{{
            summary.rcManager }}</span></div>
        <div class="summary-row"><span class="summary-label">Status of Review</span><span
            class="summary-value summary-value--strong">{{ summary.statusOfReview }}</span></div>
        <div class="summary-row"><span class="summary-label">Single/Multiple Location</span><span
            class="summary-value">{{ summary.singleMultipleLocation }}</span></div>
      </div>
    </div>

    <!-- Review Basic Details accordion -->
    <div class="accordion">
      <button type="button" class="accordion-header"
        [class.accordion-header--lavender]="basicDetailsExpanded"
        [class.accordion-header--purple]="!basicDetailsExpanded"
        (click)="toggleBasicDetails()">
        <span class="accordion-title">Review Basic Details</span>
        <span class="accordion-chevron" [class.accordion-chevron--up]="basicDetailsExpanded">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      @if (basicDetailsExpanded) {
      <div class="accordion-body">

        <!-- Row 1 -->
        <div class="form-row">
          <div class="form-field">
            <label class="field-label">
              Review Name
              <span class="required">*</span>
              <span class="info-tooltip-wrapper">
                <span class="info-icon">i</span>
              </span>
            </label>
            <textarea #reviewNameTextarea class="field-input field-input--review-name field-input--readonly"
              [(ngModel)]="form.reviewName" readonly tabindex="-1"
              rows="1"></textarea>
          </div>
          <div class="form-field">
            <label class="field-label">
              Proposed Year
              <span class="required">*</span>
              <span class="info-tooltip-wrapper">
                <span class="info-icon">i</span>
              </span>
            </label>
            @if (isDraftOrPlanned) {
            <div class="select-wrapper">
              <select class="field-select" [(ngModel)]="form.proposedYear" (ngModelChange)="onProposedYearChange()">
                <option value="" disabled>Select Year</option>
                @if (proposedYears.length === 0 && form.proposedYear) {
                <option [value]="form.proposedYear">{{ form.proposedYear }}</option>
                }
                @for (year of proposedYears; track year.ProposedYearId) {
                <option value="{{ year.ProposedYear }}">{{ year.DisplayName }}</option>
                }
              </select>
              <span class="select-chevron">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2.5">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </div>
            } @else {
            <input type="text" class="field-input field-input--readonly" [value]="proposedYearDisplayName"
              readonly tabindex="-1" placeholder="Proposed Year" />
            }
            @if (submitAttempted && !form.proposedYear) {
            <span class="field-error">Proposed Year is required.</span>
            }
          </div>
          <div class="form-field">
            <label class="field-label">
              Master Client Name
              <span class="required">*</span>
              <span class="info-tooltip-wrapper">
                <span class="info-icon">i</span>
              </span>
            </label>
            @if (isDraft) {
            <div class="searchable-select client-select">
              <div class="searchable-select__display field-select" (click)="toggleClientDropdown()"
                [class.placeholder]="!form.masterClientName">
                {{ form.masterClientName || 'Select Master Client' }}
              </div>
              <span class="select-chevron">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="2.5">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
              @if (clientDropdownOpen) {
              <div class="searchable-dropdown">
                <input type="text" class="searchable-dropdown__search" #clientSearchInput
                  [(ngModel)]="clientSearchTerm" placeholder="Search..." />
                <ul class="searchable-dropdown__list">
                  @for (name of filteredClientNames; track name) {
                  <li (mousedown)="selectClient(name)">{{ name }}</li>
                  }
                  @if (filteredClientNames.length === 0) {
                  <li class="no-results">No matches</li>
                  }
                </ul>
              </div>
              }
            </div>
            } @else {
            <input type="text" class="field-input field-input--readonly"
              [(ngModel)]="form.masterClientName" [readonly]="true" placeholder="Master Client Name" />
            }
            @if (submitAttempted && !form.masterClientName) {
            <span class="field-error">Master Client Name is required.</span>
            }
          </div>
        </div>

        <!-- Row 2 -->
        <div class="form-row">
          <div class="form-field">
            <label class="field-label">
              D&amp;T Project Name
              <span class="required">*</span>
              <span class="info-tooltip-wrapper">
                <span class="info-icon">i</span>
              </span>
            </label>
            @if (isDraft) {
            <div class="searchable-select project-select">
              <div class="searchable-select__display field-select" (click)="toggleProjectDropdown()"
                [class.placeholder]="!form.dtProjectName">
                {{ form.dtProjectName || 'Select Project' }}
              </div>
              <span class="select-chevron">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="2.5">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
              @if (projectDropdownOpen) {
              <div class="searchable-dropdown">
                <input type="text" class="searchable-dropdown__search" #projectSearchInput
                  [(ngModel)]="projectSearchTerm" placeholder="Search..." />
                <ul class="searchable-dropdown__list">
                  @for (name of filteredProjectNames; track name) {
                  <li (mousedown)="selectProject(name)">{{ name }}</li>
                  }
                  @if (filteredProjectNames.length === 0) {
                  <li class="no-results">No matches</li>
                  }
                </ul>
              </div>
              }
            </div>
            } @else {
            <input type="text" class="field-input field-input--readonly"
              [(ngModel)]="form.dtProjectName" [readonly]="true" placeholder="D&T Project Name" />
            }
            @if (submitAttempted && !form.dtProjectName) {
            <span class="field-error">D&T Project Name is required.</span>
            }
          </div>
          <div class="form-field">
            <label class="field-label">
              Contract Number
              <span class="required">*</span>
              <span class="info-tooltip-wrapper">
                <span class="info-icon">i</span>
              </span>
            </label>
            @if (isDraft) {
            <div class="select-wrapper">
              <select class="field-select" [(ngModel)]="form.contractNumber" (ngModelChange)="onContractNumberChange()">
                <option value="" disabled>Select Contract Number</option>
                @if (contractNumbers.length === 0 && form.contractNumber) {
                <option [value]="form.contractNumber">{{ form.contractNumber }}</option>
                }
                @for (contract of contractNumbers; track contract) {
                <option [value]="contract">{{ contract }}</option>
                }
              </select>
              <span class="select-chevron">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2.5">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </div>
            } @else {
            <input type="text" class="field-input field-input--readonly"
              [(ngModel)]="form.contractNumber" [readonly]="true" placeholder="Contract Number" />
            }
            @if (submitAttempted && !form.contractNumber) {
            <span class="field-error">Contract Number is required.</span>
            }
          </div>
          <div class="form-field">
            <label class="field-label">Additional Contract Number</label>
            <input type="text" class="field-input" [class.field-input--readonly]="!isDraft"
              [(ngModel)]="form.additionalContractNumber" [readonly]="!isDraft"
              (input)="onAdditionalContractNumberInput($event)" (keypress)="onAdditionalContractNumberKeyPress($event)"
              inputmode="numeric" maxlength="10" placeholder="Enter Additional Contract Number" />
            @if (additionalContractNumberError) {
            <span class="field-error">{{ additionalContractNumberError }}</span>
            }
          </div>
        </div>

        @if (!usesGoLiveLayout) {
        <div class="border-top"></div>
        }

        <!-- Row 3: Market + Market Unit shared by both review types;
             Customer Relationship Name is standard-only (3rd grid cell absent = empty column for Go-Live/ODR). -->
        <div class="form-row">
          <div class="form-field">
            <label class="field-label">Market <span class="required">*</span></label>
            <input type="text" class="field-input field-input--disabled" [value]="form.market"
              readonly tabindex="-1" placeholder="Market" />
            @if (submitAttempted && !form.market) {
            <span class="field-error">Market is required.</span>
            }
          </div>
          <div class="form-field">
            <label class="field-label">Market Unit @if (usesGoLiveLayout) {<span class="required">*</span>}</label>
            <input type="text" class="field-input field-input--disabled" [value]="form.marketUnit"
              readonly tabindex="-1" placeholder="Market Unit" />
            @if ((usesGoLiveLayout) && submitAttempted && !form.marketUnit) {
            <span class="field-error">Market Unit is required.</span>
            }
          </div>
          @if (isSpecialProject) {
          <div class="form-field">
            <label class="field-label">
              Type of Project <span class="required">*</span>
              <span class="info-tooltip-wrapper">
                <span class="info-icon">i</span>
                <span class="info-tooltip">If your project is not listed here, please contact the Admin.</span>
              </span>
            </label>
            @if (isDraft) {
            <div class="searchable-select type-of-project-select">
              <div class="searchable-select__display field-select" (click)="toggleTypeOfProjectDropdown()"
                [class.placeholder]="!form.typeOfProject"
                [class.field-select--error]="submitAttempted && !form.typeOfProject">
                {{ form.typeOfProject || 'Select Type of Project' }}
              </div>
              <span class="select-chevron">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="2.5">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
              @if (typeOfProjectDropdownOpen) {
              <div class="searchable-dropdown">
                <input type="text" class="searchable-dropdown__search" #typeOfProjectSearchInput
                  [(ngModel)]="typeOfProjectSearchTerm" placeholder="Search..." />
                <ul class="searchable-dropdown__list">
                  @for (opt of filteredTypeOfProjectOptions; track opt) {
                  <li (mousedown)="selectTypeOfProject(opt)">{{ opt }}</li>
                  }
                  @if (filteredTypeOfProjectOptions.length === 0) {
                  <li class="no-results">No matches</li>
                  }
                </ul>
              </div>
              }
            </div>
            } @else {
            <input type="text" class="field-input field-input--readonly"
              [value]="form.typeOfProject" [readonly]="true" placeholder="Type of Project" />
            }
            @if (submitAttempted && !form.typeOfProject) {
            <span class="field-error">Select the 'Type of Project'</span>
            }
          </div>
          }
          @if (!usesGoLiveLayout) {
          <div class="form-field">
            <label class="field-label">Customer Relationship Name</label>
            <input type="text" class="field-input" [class.field-input--readonly]="!isDraft"
              [(ngModel)]="form.customerRelationshipName" [readonly]="!isDraft" placeholder="Enter Customer Relationship Name" />
          </div>
          }
        </div>

        @if (isGoLive) {
        <div class="border-bottom-separator"></div>
        }

        @if (!usesGoLiveLayout) {
        <!-- Row 4 — standard only -->
        <div class="form-row form-row--top">
          <div class="form-field">
            <label class="field-label">Plan Name</label>
            <input type="text" class="field-input" [class.field-input--readonly]="!isDraft" [(ngModel)]="form.planName"
              [readonly]="!isDraft" placeholder="Enter Plan Name" />
          </div>
          <div class="form-field">
            <label class="field-label">QAD Recommendation (OPP)/QAD Overall Status (Del)</label>
            <textarea class="field-textarea" [class.field-input--readonly]="!isDraft"
              [(ngModel)]="form.qadRecommendation" [readonly]="!isDraft" placeholder="Enter QAD Recommendation (OPP)/QAD Overall Status (Del)"></textarea>
          </div>
          <div class="form-field">
            <label class="field-label">Client Quality (CQI)</label>
            @if (isDraft) {
            <div class="select-wrapper">
              <select class="field-select" [(ngModel)]="form.clientQuality">
                <option value="" disabled>Select Client Quality</option>
                @for (opt of clientQualityOptions; track opt) {
                <option [value]="opt">{{ opt }}</option>
                }
              </select>
              <span class="select-chevron">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="2.5">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </div>
            } @else {
            <input type="text" class="field-input field-input--readonly" [(ngModel)]="form.clientQuality"
              [readonly]="true" placeholder="Select Client Quality" />
            }
          </div>
        </div>

        <div class="border-top"></div>

        <!-- Row 5 — standard only -->
        <div class="form-row">
          <div class="form-field">
            <label class="field-label">Client Account Lead (CR)</label>
            <div class="input-with-icon">
              <input type="text" class="field-input" [class.field-input--readonly]="!isDraft"
                [(ngModel)]="form.clientAccountLead" [readonly]="!isDraft" placeholder="Search by EID or Name" />
              <button class="input-icon-btn" type="button">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
            </div>
          </div>
          <div class="form-field">
            <label class="field-label">Operation Account Lead - Overall</label>
            <div class="input-with-icon">
              <input type="text" class="field-input" [class.field-input--readonly]="!isDraft"
                [(ngModel)]="form.operationAccountLead" [readonly]="!isDraft" placeholder="Search by EID or Name" />
              <button class="input-icon-btn" type="button">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
            </div>
          </div>
          <div class="form-field">
            <label class="field-label">Master Client ID</label>
            <input type="text" class="field-input" [class.field-input--readonly]="!isDraft"
              [(ngModel)]="form.masterClientId" [readonly]="!isDraft" placeholder="Enter Master Client ID"
              inputmode="numeric" (input)="onMasterClientIdInput($event)" (keypress)="onMasterClientIdKeyPress($event)" />
            @if (masterClientIdError) {
            <span class="field-error">{{ masterClientIdError }}</span>
            }
          </div>
        </div>
        
      } <!-- end @if (!usesGoLiveLayout) -->

        @if (isGoLive) {
        <!-- Type of Review -->
        <div class="gl-type-of-review">
          <span class="gl-type-label">Type of Review <span class="required">*</span></span>
          <div class="gl-radio-group">
            @if (isDraft) {
            <label class="gl-radio-option">
              <input type="radio" name="glTypeOfReview" value="pre-post"
                [(ngModel)]="form.typeOfReview" />
              Pre and Post Go-Live
            </label>
            <label class="gl-radio-option">
              <input type="radio" name="glTypeOfReview" value="post-only"
                [(ngModel)]="form.typeOfReview" />
              Only the Post Go-Live stage
            </label>
            } @else {
            <label class="gl-radio-option">
              <input type="radio" name="glTypeOfReview" value="pre-post"
                [checked]="form.typeOfReview === 'pre-post'" disabled />
              Pre and Post Go-Live
            </label>
            <label class="gl-radio-option">
              <input type="radio" name="glTypeOfReview" value="post-only"
                [checked]="form.typeOfReview === 'post-only'" disabled />
              Only the Post Go-Live stage
            </label>
            }
          </div>
          @if (submitAttempted && !form.typeOfReview) {
          <span class="field-error">Type of Review is required.</span>
          }
        </div>
        } <!-- end @if (isGoLive) -->
        <!-- Go-Live location section (always rendered) -->
        <div class="gl-location-section-header">
          <h3 class="location-section-title">Location to be reviewed</h3>
        </div>

        <div class="location-review-cards">
          @if (isDraftOrPlanned) {
          <div class="gl-add-location-row">
            <button type="button" class="gl-add-location-btn" (click)="addGoLiveLocationCard()">+ Add New Location</button>
          </div>
          }
          @for (card of locationCards; track i; let i = $index) {
          <div class="location-card" [class.location-card--parent]="card.isParentLocation">

            <!-- Row 1: Delivery Country left, Set Parent + Edit/Save + optional Delete right -->
            <div class="gl-card-top-row">
              <div class="gl-card-country-field">
                <label class="field-label">Delivery Country <span class="required">*</span></label>
                @if (isGoLiveCardEditing(i) && !isFwStarted) {
                <div class="searchable-select gl-rp-country-select-{{ i }}" >
                  <div class="searchable-select__display field-select"
                    (click)="toggleGlCardCountryDropdown(i)"
                    [class.placeholder]="!card.deliveryCountry">
                    {{ card.deliveryCountry || 'Select Country' }}
                  </div>
                  <span class="select-chevron">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="2.5">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                  @if (glCountryDropdownKey === i) {
                  <div class="searchable-dropdown" (click)="$event.stopPropagation()">
                    <input type="text" class="searchable-dropdown__search" [(ngModel)]="glCountrySearchTerm" placeholder="Search..." />
                    <ul class="searchable-dropdown__list">
                      @for (c of filteredGlCardCountries(); track c.CountryMasterID) {
                      <li (mousedown)="addGlCardCountry(card, c.CountryName)">{{ c.CountryName }}</li>
                      }
                      @if (filteredGlCardCountries().length === 0) {
                      <li class="no-results">No matches</li>
                      }
                    </ul>
                  </div>
                  }
                </div>
                } @else {
                <input type="text" class="field-input field-input--readonly"
                  [value]="card.deliveryCountry" readonly placeholder="Delivery Country" />
                }
                @if ((submitAttempted || cardSaveAttempted[i]) && !card.deliveryCountry) {
                <span class="field-error">Delivery Country is required.</span>
                }
              </div>
              <div class="gl-card-actions">
                <label class="parent-radio">
                  <input type="radio" name="glParentLocation" [checked]="card.isParentLocation"
                    (change)="isGoLiveCardEditing(i) && setParentLocation(i)" [disabled]="!isGoLiveCardEditing(i)" />
                  <span>Set Parent Location</span>
                </label>
                @if (!isReadOnlyAccess && !isIMPublishedIssuesClosed) {
                  @if (isTeamFieldEditing(i)) {
                  <button class="gl-rp-card-edit-btn" type="button" (click)="saveGoLiveCard(i)" title="Save">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
                      <polyline points="17 21 17 13 7 13 7 21"/>
                      <polyline points="7 3 7 8 15 8"/>
                    </svg>
                  </button>
                  } @else {
                  <button class="gl-rp-card-edit-btn" type="button" (click)="startEditGoLiveCard(i)" title="Edit">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                  }
                }
                @if (isDraftOrPlanned && locationCards.length > 1) {
                <button type="button" class="location-card-delete"
                  (click)="usesGoLiveLayout ? deleteGoLiveLocationCard(i) : deleteLocationCard(i)"
                  aria-label="Delete location">
                  <img src="images/Trashbin.png" alt="Delete" />
                </button>
                }
              </div>
            </div>

            <!-- Row 2: Location / Quarter / Proposed Month -->
            <div class="location-row">
              <div class="location-field">
                <label class="field-label">Location to be reviewed <span class="required">*</span></label>
                @if (isGoLiveCardEditing(i) && !isFwStarted) {
                <div class="searchable-select gl-rp-city-select-{{ i }}"
                  [class.field-input--disabled]="!card.deliveryCountry">
                  <div class="searchable-select__display field-select"
                    (click)="card.deliveryCountry && toggleGlCityDropdown(i)"
                    [class.placeholder]="!card.location">
                    {{ card.location || 'Select Location' }}
                  </div>
                  @if (card.deliveryCountry) {
                  <span class="select-chevron">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="2.5">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                  }
                  @if (glCityDropdownKey === i) {
                  <div class="searchable-dropdown" (click)="$event.stopPropagation()">
                    <input type="text" class="searchable-dropdown__search" [(ngModel)]="glCitySearchTerm" placeholder="Search..." />
                    <ul class="searchable-dropdown__list">
                      @for (city of filteredGlCitiesForCard(card); track city.CityID) {
                      <li (mousedown)="selectGlCity(card, city.CityName)">{{ city.CityName }}</li>
                      }
                      @if (filteredGlCitiesForCard(card).length === 0) {
                      <li class="no-results">No matches</li>
                      }
                    </ul>
                  </div>
                  }
                </div>
                } @else {
                <input type="text" class="field-input field-input--readonly"
                  [value]="card.location" readonly placeholder="Location" />
                }
                @if ((submitAttempted || cardSaveAttempted[i]) && !card.location) {
                <span class="field-error">Location to be reviewed is required.</span>
                }
                @if (locationCardErrors[i]?.duplicate) {
                <span class="field-error">{{ locationCardErrors[i].duplicate }}</span>
                }
              </div>
              <div class="location-field">
                <label class="field-label">Quarter</label>
                <div class="select-wrapper">
                  <select class="field-select" [(ngModel)]="card.quarter" [disabled]="!isGoLiveCardEditing(i) || isFwStarted"
                    [class.field-input--readonly]="!isGoLiveCardEditing(i) || isFwStarted"
                    (ngModelChange)="onQuarterChange(card)">
                    <option value="" disabled>Select Quarter</option>
                    @for (q of quarterOptions; track q) {
                    <option [value]="q">{{ q }}</option>
                    }
                  </select>
                  <span class="select-chevron">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2.5">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </div>
              </div>
              <div class="location-field">
                <label class="field-label">Proposed Month</label>
                <div class="select-wrapper">
                  <select class="field-select" [(ngModel)]="card.proposedMonth"
                    [disabled]="!isGoLiveCardEditing(i) || !card.quarter || isFwStarted"
                    [class.field-input--readonly]="!isGoLiveCardEditing(i) || isFwStarted">
                    <option value="" disabled>Select Proposed Month</option>
                    @if (showSavedMonthOption(card)) {
                    <option [value]="card.proposedMonth">{{ card.proposedMonth }}</option>
                    }
                    @for (m of getMonthsForQuarter(card.quarter); track m) {
                    <option [value]="m">{{ m }}</option>
                    }
                  </select>
                  <span class="select-chevron">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2.5">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>

            <!-- Row 3: Primary R&C Manager* / Supervisor* / Reviewer* (magnifying-glass icon) -->
            <div class="location-row">
              <div class="location-field" [id]="'primary-manager-field-' + i">
                <label class="field-label">R&amp;C Manager <span class="required">*</span></label>
                @if (isTeamFieldEditing(i) && canEditRoleField(i, 'rcManager')) {
                <div class="searchable-select manager-select">
                  <div class="searchable-select__display field-select"
                    (click)="toggleManagerDropdown('gl-mgr-' + i)" tabindex="0"
                    (keydown.backspace)="clearRoleField(card, 'rcManager', i, $event)" (keydown.delete)="clearRoleField(card, 'rcManager', i, $event)" [class.placeholder]="!card.rcManager">
                    {{ managerLabel(card.rcManager) || 'Search by EID or Name' }}
                  </div>
                  <span class="select-chevron">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2">
                      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </span>
                  @if (managerDropdownKey === 'gl-mgr-' + i) {
                  <div class="searchable-dropdown">
                    <input type="text" class="searchable-dropdown__search" [(ngModel)]="managerSearchTerm" placeholder="Search..." />
                    <ul class="searchable-dropdown__list">
                      @for (mgr of filteredManagers; track mgr.value) {
                      <li (mousedown)="selectManager(card, 'rcManager', mgr.value, i)">{{ mgr.label }}</li>
                      }
                      @if (filteredManagers.length === 0) {
                      <li class="no-results">No matches</li>
                      }
                    </ul>
                  </div>
                  }
                </div>
                } @else {
                <input type="text" class="field-input field-input--readonly"
                  [value]="managerLabel(card.rcManager)" readonly tabindex="-1"
                  placeholder="Search by EID or Name" />
                }
                @if ((submitAttempted || cardSaveAttempted[i]) && !card.rcManager) {
                <span class="field-error">R&amp;C Manager is required.</span>
                }
                @if (schedulePrimaryTeamErrors[i]?.manager) {
                <span class="field-error">{{ schedulePrimaryTeamErrors[i].manager }}</span>
                }
              </div>
              <div class="location-field" [id]="'primary-supervisor-field-' + i">
                <label class="field-label">R&amp;C Supervisor <span class="required">*</span></label>
                @if (isTeamFieldEditing(i) && canEditRoleField(i, 'rcSupervisor')) {
                <div class="searchable-select supervisor-select">
                  <div class="searchable-select__display field-select"
                    (click)="toggleSupervisorDropdown('gl-sup-' + i)" tabindex="0"
                    (keydown.backspace)="clearRoleField(card, 'rcSupervisor', i, $event)" (keydown.delete)="clearRoleField(card, 'rcSupervisor', i, $event)" [class.placeholder]="!card.rcSupervisor">
                    {{ supervisorLabel(card.rcSupervisor) || 'Search by EID or Name' }}
                  </div>
                  <span class="select-chevron">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2">
                      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </span>
                  @if (supervisorDropdownKey === 'gl-sup-' + i) {
                  <div class="searchable-dropdown">
                    <input type="text" class="searchable-dropdown__search" [(ngModel)]="supervisorSearchTerm" placeholder="Search..." />
                    <ul class="searchable-dropdown__list">
                      @for (sup of filteredSupervisors; track sup.value) {
                      <li (mousedown)="selectSupervisor(card, 'rcSupervisor', sup.value, i)">{{ sup.label }}</li>
                      }
                      @if (filteredSupervisors.length === 0) {
                      <li class="no-results">No matches</li>
                      }
                    </ul>
                  </div>
                  }
                </div>
                } @else {
                <input type="text" class="field-input field-input--readonly"
                  [value]="supervisorLabel(card.rcSupervisor)" readonly tabindex="-1"
                  placeholder="Search by EID or Name" />
                }
                @if ((submitAttempted || cardSaveAttempted[i]) && !card.rcSupervisor) {
                <span class="field-error">R&amp;C Supervisor is required.</span>
                }
                @if (schedulePrimaryTeamErrors[i]?.supervisor) {
                <span class="field-error">{{ schedulePrimaryTeamErrors[i].supervisor }}</span>
                }
              </div>
              <div class="location-field" [id]="'primary-reviewer-field-' + i">
                <label class="field-label">R&amp;C Reviewer <span class="required">*</span></label>
                @if (isTeamFieldEditing(i) && canEditRoleField(i, 'rcReviewer')) {
                <div class="searchable-select reviewer-select">
                  <div class="searchable-select__display field-select"
                    (click)="toggleReviewerDropdown('gl-rev-' + i)" tabindex="0"
                    (keydown.backspace)="clearRoleField(card, 'rcReviewer', i, $event)" (keydown.delete)="clearRoleField(card, 'rcReviewer', i, $event)" [class.placeholder]="!card.rcReviewer">
                    {{ reviewerLabel(card.rcReviewer) || 'Search by EID or Name' }}
                  </div>
                  <span class="select-chevron">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2">
                      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </span>
                  @if (reviewerDropdownKey === 'gl-rev-' + i) {
                  <div class="searchable-dropdown">
                    <input type="text" class="searchable-dropdown__search" [(ngModel)]="reviewerSearchTerm" placeholder="Search..." />
                    <ul class="searchable-dropdown__list">
                      @for (rev of filteredReviewers; track rev.value) {
                      <li (mousedown)="selectReviewer(card, 'rcReviewer', rev.value, i)">{{ rev.label }}</li>
                      }
                      @if (filteredReviewers.length === 0) {
                      <li class="no-results">No matches</li>
                      }
                    </ul>
                  </div>
                  }
                </div>
                } @else {
                <input type="text" class="field-input field-input--readonly"
                  [value]="reviewerLabel(card.rcReviewer)" readonly tabindex="-1"
                  placeholder="Search by EID or Name" />
                }
                @if ((submitAttempted || cardSaveAttempted[i]) && !card.rcReviewer) {
                <span class="field-error">R&amp;C Reviewer is required.</span>
                }
                @if (schedulePrimaryTeamErrors[i]?.reviewer) {
                <span class="field-error">{{ schedulePrimaryTeamErrors[i].reviewer }}</span>
                }
              </div>
            </div>

            <!-- Row 4: Secondary R&C Manager / Supervisor / Reviewer -->
            <div class="location-row">
              <div class="location-field">
                <label class="field-label">Secondary R&amp;C Manager</label>
                @if (isTeamFieldEditing(i) && canEditRoleField(i, 'secondaryRcManager')) {
                <div class="searchable-select manager-select">
                  <div class="searchable-select__display field-select"
                    (click)="toggleManagerDropdown('gl-sec-mgr-' + i)" tabindex="0"
                    (keydown.backspace)="clearRoleField(card, 'secondaryRcManager', i, $event)" (keydown.delete)="clearRoleField(card, 'secondaryRcManager', i, $event)" [class.placeholder]="!card.secondaryRcManager">
                    {{ managerLabel(card.secondaryRcManager) || 'Search by EID or Name' }}
                  </div>
                  <span class="select-chevron">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2">
                      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </span>
                  @if (managerDropdownKey === 'gl-sec-mgr-' + i) {
                  <div class="searchable-dropdown">
                    <input type="text" class="searchable-dropdown__search" [(ngModel)]="managerSearchTerm" placeholder="Search..." />
                    <ul class="searchable-dropdown__list">
                      @for (mgr of filteredManagers; track mgr.value) {
                      <li (mousedown)="selectManager(card, 'secondaryRcManager', mgr.value, i)">{{ mgr.label }}</li>
                      }
                      @if (filteredManagers.length === 0) {
                      <li class="no-results">No matches</li>
                      }
                    </ul>
                  </div>
                  }
                </div>
                } @else {
                <input type="text" class="field-input field-input--readonly"
                  [value]="managerLabel(card.secondaryRcManager)" readonly tabindex="-1"
                  placeholder="Search by EID or Name" />
                }
                @if (locationCardErrors[i]?.manager) {
                <span class="field-error">{{ locationCardErrors[i].manager }}</span>
                }
              </div>
              <div class="location-field">
                <label class="field-label">Secondary R&amp;C Supervisor</label>
                @if (isTeamFieldEditing(i) && canEditRoleField(i, 'secondaryRcSupervisor')) {
                <div class="searchable-select supervisor-select">
                  <div class="searchable-select__display field-select"
                    (click)="toggleSupervisorDropdown('gl-sec-sup-' + i)" tabindex="0"
                    (keydown.backspace)="clearRoleField(card, 'secondaryRcSupervisor', i, $event)" (keydown.delete)="clearRoleField(card, 'secondaryRcSupervisor', i, $event)" [class.placeholder]="!card.secondaryRcSupervisor">
                    {{ supervisorLabel(card.secondaryRcSupervisor) || 'Search by EID or Name' }}
                  </div>
                  <span class="select-chevron">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2">
                      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </span>
                  @if (supervisorDropdownKey === 'gl-sec-sup-' + i) {
                  <div class="searchable-dropdown">
                    <input type="text" class="searchable-dropdown__search" [(ngModel)]="supervisorSearchTerm" placeholder="Search..." />
                    <ul class="searchable-dropdown__list">
                      @for (sup of filteredSupervisors; track sup.value) {
                      <li (mousedown)="selectSupervisor(card, 'secondaryRcSupervisor', sup.value, i)">{{ sup.label }}</li>
                      }
                      @if (filteredSupervisors.length === 0) {
                      <li class="no-results">No matches</li>
                      }
                    </ul>
                  </div>
                  }
                </div>
                } @else {
                <input type="text" class="field-input field-input--readonly"
                  [value]="supervisorLabel(card.secondaryRcSupervisor)" readonly tabindex="-1"
                  placeholder="Search by EID or Name" />
                }
                @if (locationCardErrors[i]?.supervisor) {
                <span class="field-error">{{ locationCardErrors[i].supervisor }}</span>
                }
              </div>
              <div class="location-field">
                <label class="field-label">Secondary R&amp;C Reviewer</label>
                @if (isTeamFieldEditing(i) && canEditRoleField(i, 'secondaryRcReviewer')) {
                <div class="searchable-select reviewer-select">
                  <div class="searchable-select__display field-select"
                    (click)="toggleReviewerDropdown('gl-sec-rev-' + i)" tabindex="0"
                    (keydown.backspace)="clearRoleField(card, 'secondaryRcReviewer', i, $event)" (keydown.delete)="clearRoleField(card, 'secondaryRcReviewer', i, $event)" [class.placeholder]="!card.secondaryRcReviewer">
                    {{ reviewerLabel(card.secondaryRcReviewer) || 'Search by EID or Name' }}
                  </div>
                  <span class="select-chevron">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2">
                      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </span>
                  @if (reviewerDropdownKey === 'gl-sec-rev-' + i) {
                  <div class="searchable-dropdown">
                    <input type="text" class="searchable-dropdown__search" [(ngModel)]="reviewerSearchTerm" placeholder="Search..." />
                    <ul class="searchable-dropdown__list">
                      @for (rev of filteredReviewers; track rev.value) {
                      <li (mousedown)="selectReviewer(card, 'secondaryRcReviewer', rev.value, i)">{{ rev.label }}</li>
                      }
                      @if (filteredReviewers.length === 0) {
                      <li class="no-results">No matches</li>
                      }
                    </ul>
                  </div>
                  }
                </div>
                } @else {
                <input type="text" class="field-input field-input--readonly"
                  [value]="reviewerLabel(card.secondaryRcReviewer)" readonly tabindex="-1"
                  placeholder="Search by EID or Name" />
                }
                @if (locationCardErrors[i]?.reviewer) {
                <span class="field-error">{{ locationCardErrors[i].reviewer }}</span>
                }
              </div>
            </div>

          </div>
          }
        </div>

        <!-- Comments & Attachment -->
        <div class="comments-attachment-row">
          <div class="comments-field">
            <label class="field-label">Comments</label>
            <textarea class="field-textarea comments-textarea" [class.field-input--readonly]="isScheduled || isReadOnlyAccess"
              [(ngModel)]="form.comments" [readonly]="isScheduled || isReadOnlyAccess"></textarea>
          </div>
          <div class="attachment-field">
            <label class="field-label">Attachment</label>

            <a class="upload-link" [class.upload-link--disabled]="isScheduled || isReadOnlyAccess"
              (click)="isScheduled || isReadOnlyAccess ? null : reviewFileInput.click()">
              <span class="upload-icon">i</span>
              Agreement Document
            </a>
            <input #reviewFileInput type="file" multiple hidden (change)="onFilesSelected($event)" />
            @if (!isPlannedChild || usesGoLiveLayout) {
            @for (doc of prePopulatedDocuments; track doc.filePath; let i = $index) {
            <div class="attachment-row">
              <a class="attachment-link attachment-link--downloadable" [title]="doc.filePath"
                (click)="downloadPrePopulatedDoc(doc)">{{ doc.fileName }}</a>
              @if (!isScheduled && !isReadOnlyAccess) {
              <button class="attachment-trash" type="button" (click)="removePrePopulatedDoc(i)"
                aria-label="Remove attachment">
                <img src="images/Trashbin.png" alt="Delete" />
              </button>
              }
            </div>
            }
            @for (file of attachedFiles; track file.name; let i = $index) {
            <div class="attachment-row">
              <a class="attachment-link attachment-link--downloadable" [title]="file.name"
                (click)="downloadAttachedFile(file)">{{ file.name }}</a>
              @if (!isScheduled && !isReadOnlyAccess) {
              <button class="attachment-trash" type="button" (click)="removeAttachedFile(i)"
                aria-label="Remove file">
                <img src="images/Trashbin.png" alt="Delete" />
              </button>
              }
            </div>
            }
            @for (err of attachmentErrors; track $index) {
            <div class="attachment-error">{{ err }}</div>
            }
            }
          </div>
        </div>

         <!-- Action Buttons -->
        <div class="action-buttons">
          <button class="action-cancel" type="button" [disabled]="!isFormDirty" (click)="onCancel()">Cancel</button>
          @if (isDraft) {
          <button class="action-submit" type="button" (click)="saveAsDraft()">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
            Save As Draft
          </button>
          }
        <!-- Action Buttons -->
          <button class="action-submit" type="button" [disabled]="isReadOnlyAccess || (!isDraft && (isLoading || !isFormDirty))" (click)="submitForm()">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
            Submit
          </button>
        </div>
      </div>
      }
    </div>

    <!-- Collapsed sections -->
    <div class="collapsed-sections">
          <div class="schedule-btn-wrapper">
            <button type="button" class="accordion-header"
              [class.accordion-header--lavender]="scheduleExpanded"
              [class.accordion-header--purple]="!scheduleExpanded"
              [disabled]="isStatusDraft"
              (mouseenter)="onScheduleMouseEnter($event)"
              (mouseleave)="isScheduleHovered = false"
              (click)="toggleSchedule()">
              <span class="accordion-title">Schedule</span>
              <span class="accordion-chevron" [class.accordion-chevron--up]="scheduleExpanded">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </button>
            @if (isStatusDraft && isScheduleHovered) {
              <span class="schedule-draft-tooltip" [style.left.px]="scheduleTooltipX">Scheduling is available after submission.</span>
            }
          </div>

      @if (scheduleExpanded) {
        <div [class.rc-readonly]="isCancelled || (isReadOnlyAccess && !isUnassignedActive)" [class.rc-readonly--active-unassigned]="isUnassignedActive">
          <app-schedule
            [reviewId]="summary.reviewId"
            [singleMultipleLocation]="summary.singleMultipleLocation"
            [isParentLocation]="summary.isParentLocation"
            [reviewStatus]="summary.statusOfReview"
            [savedFieldworkStartDate]="summary.planFieldworkStartDate"
            [savedFieldworkEndDate]="summary.planFieldworkEndDate"
            [savedServiceLine]="summary.serviceLine"
            [savedParentChildType]="summary.parentChildType"
            [savedParentReviewId]="summary.parentReviewId"
            [savedTargetPublicationDate]="summary.targetPublicationDate"
            [basicDetailsDirty]="isPlanned && isFormDirty"
            [locationCards]="locationCards"
            [isManagerRole]="isManagerRole"
            [isGoLive]="isGoLive"
            [typeOfReview]="summary.typeOfReview ?? ''"
            [goLiveDate]="summary.goLiveDate ?? ''"
            [preGoLiveFWStartDate]="summary.preGoLiveFWStartDate ?? ''"
            [preGoLiveFWEndDate]="summary.preGoLiveFWEndDate ?? ''"
            [postGoLiveFWStartDate]="summary.postGoLiveFWStartDate ?? ''"
            [postGoLiveFWEndDate]="summary.postGoLiveFWEndDate ?? ''"
            [postGoLiveFWEndManuallyEdited]="postGoLiveFWEndManuallyEdited"
            (scheduled)="onScheduled($event)"
            (missingTeamMembers)="onMissingTeamMembers($event)"
            (scrollToLocations)="onScrollToLocations()">
          </app-schedule>
        </div>
      }

      <button type="button" class="accordion-header accordion-header--purple" (click)="toggleReviewCycle()" [class.accordion-open]="reviewCycleExpanded"
        [disabled]="(isStatusDraft || isPlanned)" [title]="(isStatusDraft || isPlanned) ? 'Review Cycle is available only when status is Scheduled.' : ''">
        <span class="accordion-title" [class.accordion-open]="reviewCycleExpanded">Review Cycle</span>
        <span class="accordion-chevron" [class.accordion-chevron--up]="reviewCycleExpanded">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      @if (reviewCycleExpanded) {
      <div [class.rc-readonly--member]="isReadOnlyAccess && isRCMember" [class.rc-readonly--admin]="isReadOnlyAccess && isAdminRole" [class.rc-readonly--active-unassigned]="isUnassignedActive">
        <app-review-cycle [reviewId]="summary.reviewId" [reviewName]="summary.reviewName"
          [reviewTypeId]="reviewTypeId"
          [isCancelled]="isCancelled"
          [isReadOnly]="isReadOnlyAccess"
          [typeOfReview]="summary.typeOfReview"
          [rcManager]="summary.rcManager"
          (reviewStatusChange)="onReviewStatusChange($event)"
          (toastNotification)="onToastNotification($event)"></app-review-cycle>
        </div>
      }

    <button type="button" class="accordion-header" [class.accordion-header--lavender]="issueManagementExpanded"
        [class.accordion-header--purple]="!issueManagementExpanded" (click)="toggleIssueManagement()"
        [disabled]="isGoLive ? (!isIMPublishedGoLive && !isIMPublishedIssuesClosed) : (!isIMPublished && !isIMPublishedIssuesClosed)"
        [title]="isGoLive ? ((!isIMPublishedGoLive && !isIMPublishedIssuesClosed) ? 'Issue Management is available only when status is IM - Published - Issue Management' : '') : ((!isIMPublished && !isIMPublishedIssuesClosed) ? 'Issue Management is available only when status is IM Published' : '')">
        <span class="accordion-title">Issue Management</span>
        <span class="accordion-chevron" [class.accordion-chevron--up]="issueManagementExpanded">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      @if (issueManagementExpanded) {
      <div [class.rc-readonly]="isReadOnlyAccess && !isRCMember && !isAdminRole && !isUnassignedActive" [class.rc-readonly--member]="isReadOnlyAccess && isRCMember" [class.rc-readonly--admin]="isReadOnlyAccess && isAdminRole" [class.rc-readonly--active-unassigned]="isUnassignedActive">
        <app-issue-management
          [reviewId]="reviewId"
          [financialYear]="financialYear"
          [quarter]="quarter"
          [isCancelled]="isCancelled"
          [reviewTypeId]="reviewTypeId"
          [isReadOnly]="isUnassignedActive"
          [isViewMode]="isViewMode"
          [typeOfReview]="summary.typeOfReview ?? ''">
        </app-issue-management>
      </div>
      }

      <button type="button" class="accordion-header" [class.accordion-header--lavender]="actionPlanExpanded"
          [class.accordion-header--purple]="!actionPlanExpanded" (click)="toggleActionPlan()"
          [disabled]="isGoLive ? (!isIMPublishedGoLive && !isIMPublishedIssuesClosed) : (!isIMPublished && !isIMPublishedIssuesClosed)"
          [title]="isGoLive ? ((!isIMPublishedGoLive && !isIMPublishedIssuesClosed) ? 'Action Plan is available only when status is IM - Published - Issue Management' : '') : ((!isIMPublished && !isIMPublishedIssuesClosed) ? 'Action Plan is available only when status is IM Published' : '')">
        <span class="accordion-title">Action Plan</span>
        <span class="accordion-chevron" [class.accordion-chevron--up]="actionPlanExpanded">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      @if (actionPlanExpanded) {
        <div [class.rc-readonly]="isReadOnlyAccess && !isRCMember && !isAdminRole && !isUnassignedActive" [class.rc-readonly--member]="isReadOnlyAccess && isRCMember" [class.rc-readonly--admin]="isReadOnlyAccess && isAdminRole" [class.rc-readonly--active-unassigned]="isUnassignedActive">
          <app-action-plan [reviewId]="reviewId" [isCancelled]="isCancelled" [reviewTypeId]="reviewTypeId" [isReadOnly]="isUnassignedActive" [isViewMode]="isViewMode"></app-action-plan>
        </div>
      }
    </div>

  </div>

<!-- ─── Cancel Review Popup ─── -->
@if (showCancelReviewModal) {
<div class="cr-overlay" (click)="closeCancelReviewModal()">
  <div class="cr-popup" (click)="$event.stopPropagation()">

    <div class="cr-header">
      <h2 class="cr-title">Review Cancellation</h2>
      <button class="cr-close" (click)="closeCancelReviewModal()">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="12" r="11" fill="#333" />
          <path d="M8 8l8 8M16 8l-8 8" stroke="#fff" stroke-width="2" stroke-linecap="round" />
        </svg>
      </button>
    </div>

    <div class="cr-body">

      <div class="cr-form-row">
        <span class="cr-form-label">Title</span>
        <span class="cr-form-value cr-form-value--bold">Review Cancellation</span>
      </div>

      <div class="cr-form-row">
        <span class="cr-form-label">Subtitle</span>
        <span class="cr-form-value">Review Cancellation action is permanent and cannot be undo</span>
      </div>

      <div class="cr-form-row cr-form-row--top">
        <span class="cr-form-label">Attachment</span>
        <div class="cr-form-value">
          <a class="upload-link" (click)="crFileInput.click()">
            <span class="material-symbols-outlined">attach_file</span>
            Upload Document
          </a>
          <input #crFileInput type="file" hidden
            accept=".pdf,.msg,.xls,.xlsx,.doc,.docx,.txt,.ppt,.pptx,.jpg,.jpeg,.png,.zip"
            (change)="onCancelReviewFileSelected($event)" />
          @if (cancelReviewFile) {
            <div class="attachment-row">
              <a class="attachment-link">{{ cancelReviewFile.name }}</a>
              <button class="attachment-trash" type="button" (click)="removeCancelReviewFile()" aria-label="Remove attachment">
                <img src="images/Trashbin.png" alt="Delete" />
              </button>
            </div>
          }
          @if (cancelReviewFileError) {
            <span class="attachment-error">{{ cancelReviewFileError }}</span>
          }
        </div>
      </div>

      <div class="cr-form-row cr-form-row--top">
        <span class="cr-form-label">Comment/Reason <span class="cr-required">*</span></span>
        <div class="cr-form-value">
          <textarea class="cr-textarea" rows="4" [(ngModel)]="cancelReviewComment"
            (ngModelChange)="cancelReviewCommentError = false"
            placeholder="Enter reason for cancellation"></textarea>
          @if (cancelReviewCommentError) {
            <span class="cr-error">Comments/Reason is required.</span>
          }
        </div>
      </div>

    </div>

    <div class="cr-footer">
      <button class="cr-btn-cancel" (click)="closeCancelReviewModal()">Cancel</button>
      <button class="cr-btn-send"
        [disabled]="!isCancelReviewFormDirty || isSendingCancelRequest"
        (click)="sendCancelReviewRequest()">
        {{ isSendingCancelRequest ? 'Sending...' : 'Send Request' }}
      </button>
    </div>

  </div>
</div>
}

<!-- Error Popup -->
@if (showErrorPopup) {
<div class="error-popup-overlay" (click)="closeErrorPopup()">
  <div class="error-popup" (click)="$event.stopPropagation()">
    <div class="error-popup-header">
      <span class="error-popup-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" fill="#dc2626" />
          <rect x="11" y="6" width="2" height="8" rx="1" fill="#fff" />
          <rect x="11" y="16" width="2" height="2" rx="1" fill="#fff" />
        </svg>
      </span>
      <h2 class="error-popup-title">Unable to Save</h2>
      <button class="error-popup-close" (click)="closeErrorPopup()">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="12" r="11" fill="#333" />
          <path d="M8 8l8 8M16 8l-8 8" stroke="#fff" stroke-width="2" stroke-linecap="round" />
        </svg>
      </button>
    </div>
    <div class="error-popup-body">
      @for (message of errorPopupMessages; track message) {
      <p class="error-popup-message">{{ message }}</p>
      }
    </div>
    <div class="error-popup-footer">
      <button class="error-popup-ok" (click)="closeErrorPopup()">OK</button>
    </div>
  </div>
</div>
}
</main>